/** @jsx jsx */
import {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {jsx, css} from '@emotion/react'

import {isMac, isUnsupportedOS, isWindows} from '../../../util/userAgent'
import {logDplWithExtensionParams, EVENTS} from '../../../util/dpl'
import {getCountry} from '../../../util/getCountry'
import {colors} from '../../../constants'
import {macDownloadLink, windowsDownloadLink} from '../../../util/browserDownloadLinks'

import MacLogo from '../../../images/browser/mac-logo.svg'
import WindowsLogo from '../../../images/browser/windows-logo.svg'

const buttonStylesDefault = css`
    font-family: 'Proxima Nova', sans-serif;
    border-radius: 10px;
    display: inline-block;
    font-family: inherit;
    letter-spacing: 0.02rem;
    line-height: 1.2;
    transition: background-color 0.3s cubic-bezier(0, 0.4, 0.74, 0.63);
    background-color: ${colors.teal};
    border: none;
    color: ${colors.midnight};
    padding: 12px 30px;
    font-weight: 600;
    box-shadow: 0px 20px 26px rgba(0, 0, 0, 0.1);
    .button-inner {
        display: flex;
        align-items: center;
        height: 29px;
        .de-text {
            font-size: 13px;
        }
        .nl-text {
            font-size: 14px;
        }
        .en-text {
            font-size: 16px;
        }
    }
    .logo {
        margin-right: 10px;
    }
    &:hover {
        cursor: pointer;
        background-color: ${colors.tealLight2};
    }
`

const buttonStylesHeader = css`
    font-family: 'Proxima Nova', sans-serif;
    border-radius: 10px;
    display: inline-block;
    font-family: inherit;
    letter-spacing: 0.02rem;
    line-height: 1.2;
    transition: background-color 0.3s cubic-bezier(0, 0.4, 0.74, 0.63);
    background-color: ${colors.brandBlueLight3};
    border: none;
    color: ${colors.whisperLight};
    padding: 8px 14px;
    font-weight: 600;
    .button-inner {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 21px;
        .de-text {
            font-size: 11px;
        }
        .nl-text {
            font-size: 12px;
        }
        .en-text {
            font-size: 14px;
        }
    }
    .logo {
        margin-right: 10px;
        filter: brightness(0) invert(1);
        svg {
            height: 17px;
            width: 14px;
        }
    }
    .text {
        color: ${colors.whisperLight};
        text-align: center;
    }
    &:hover {
        cursor: pointer;
        background-color: ${colors.brandBlueLight4};
    }
`

const Button = ({
    text,
    experiment,
    onClick,
    ctaType,
    ariaLabel,
    tabIndex = '0',
    languageCode = 'en',
    styleVariant = `default`,
    overrideStyles,
    os,
    browser,
    link = 'downloadLink',
}) => {
    const [browserLogo, setBrowserLogo] = useState(null)
    const [ctaLink, setCtaLink] = useState('')

    useEffect(() => {
        if (isUnsupportedOS()) {
            return
        }
        if (link === 'downloadLink') {
            if (isMac()) {
                setBrowserLogo(<MacLogo />)
                setCtaLink(macDownloadLink)
            }
            if (isWindows()) {
                setBrowserLogo(<WindowsLogo />)
                setCtaLink(windowsDownloadLink)
            }
        } else {
            setCtaLink(link)
        }
    }, [])

    const onBtnClickDownload = () => {
        logDplWithExtensionParams(EVENTS.StartpageClick, {
            os,
            browser,
            country: getCountry(),
            type: ctaType,
            experiment,
        }).then(() => {
            const event = new CustomEvent('showSuccessModal', {detail: {ctaType}})
            window.dispatchEvent(event)
        })
    }

    const onBtnClickWaitlist = (event) => {
        event.preventDefault()
        const newsletterSection = document.querySelector(ctaLink)
        window.scrollTo({
            // @ts-ignore
            top: newsletterSection.offsetTop,
            behavior: 'smooth',
        })
    }

    return (
        <a
            href={ctaLink}
            download
            className="conversion-button"
            onClick={onClick || link === 'downloadLink' ? onBtnClickDownload : onBtnClickWaitlist}
            aria-label={ariaLabel}
            css={css`
                ${styleVariant === 'header' ? buttonStylesHeader : buttonStylesDefault}
                ${overrideStyles}
            `}
            tabIndex={tabIndex}
            type="button"
        >
            <div
                className="button-inner"
                css={css`
                    transition: opacity 0.2s ease;
                `}
            >
                <span className={`text ${languageCode}-text`}>{text}</span>
            </div>
        </a>
    )
}

Button.propTypes = {
    experiment: PropTypes.string,
    onClick: PropTypes.func,
    ctaType: PropTypes.string.isRequired,
    text: PropTypes.string,
    ariaLabel: PropTypes.string,
    overrideStyles: PropTypes.string,
}

Button.defaultProps = {
    experiment: `control`,
    // ctaType: `cta-1`,
    text: `Add to Firefox`,
    ariaLabel: `Add to Firefox`,
    overrideStyles: ``,
}

export default Button
